import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { IClinicGroup, IClinicSummary, IUpdateClinicGroupPayload } from 'src/app/models/clinic-group.model';
import { IError } from 'src/app/models/error.model';
import { setWarningText } from 'src/app/reducers/warning-popup.reducer';
import { ClinicGroupService } from 'src/app/services/clinic-group.service';
import { CompanyService } from 'src/app/services/company.service';
import { ContextService } from 'src/app/services/context.service';
import { IClinicContext } from 'src/app/models/clinic-context.model';

@Component({
  selector: 'app-clinic-group-detail',
  templateUrl: './clinic-group-detail.component.html',
  styleUrls: ['./clinic-group-detail.component.sass']
})
export class ClinicGroupDetailComponent implements OnInit, OnDestroy {
  loading = false;
  saving = false;
  groupId = '';
  group: IClinicGroup | null = null;
  groupForm!: FormGroup;

  // Sharing flags
  sharePatients = false;
  shareProcedures = false;
  shareProviders = false;
  savingSharing = false;

  // Assign clinic
  showAssignModal = false;
  assignSearchTerm = '';
  availableClinics: any[] = [];
  filteredAvailableClinics: any[] = [];
  loadingClinics = false;

  // Remove clinic
  showRemoveModal = false;
  clinicToRemove: IClinicSummary | null = null;
  removingClinic = false;

  // Assign loading
  assigningClinic = false;

  // Delete group
  showDeleteModal = false;

  private subscriptions: Subscription[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private clinicGroupService: ClinicGroupService,
    private companyService: CompanyService,
    private contextService: ContextService,
    private store: Store<{ warningPopup: IError }>
  ) {}

  ngOnInit(): void {
    this.groupId = this.route.snapshot.paramMap.get('id') || '';
    this.buildForm();
    this.loadGroup();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  private buildForm(): void {
    this.groupForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]]
    });
  }

  loadGroup(): void {
    if (!this.groupId) return;
    this.loading = true;

    const sub = this.clinicGroupService.getGroupById(this.groupId).subscribe({
      next: (group) => {
        this.group = group;
        this.groupForm.patchValue({ name: group.name });
        this.sharePatients = group.share_patients || false;
        this.shareProcedures = group.share_procedures || false;
        this.shareProviders = group.share_providers || false;
        this.loading = false;
      },
      error: (error) => {
        console.error('Erro ao carregar grupo:', error);
        this.store.dispatch(setWarningText(error?.message || 'Erro ao carregar grupo.', error?.status || 500));
        this.loading = false;
        this.router.navigate(['/main/v2/clinic-groups']);
      }
    });
    this.subscriptions.push(sub);
  }

  saveName(): void {
    if (this.groupForm.invalid || !this.groupId) {
      this.groupForm.markAllAsTouched();
      return;
    }

    this.saving = true;
    const payload = { name: this.groupForm.value.name.trim() };

    const sub = this.clinicGroupService.updateGroup(this.groupId, payload).subscribe({
      next: (updated) => {
        this.group = updated;
        this.saving = false;
        this.store.dispatch(setWarningText('Nome do grupo atualizado com sucesso!'));
      },
      error: (error) => {
        console.error('Erro ao atualizar grupo:', error);
        this.store.dispatch(setWarningText(error?.message || 'Erro ao atualizar grupo.', error?.status || 500));
        this.saving = false;
      }
    });
    this.subscriptions.push(sub);
  }

  // ─── Assign Clinic ───────────────────────────────────────────────

  saveSharing(): void {
    if (!this.groupId || this.savingSharing) return;

    this.savingSharing = true;
    const payload: IUpdateClinicGroupPayload = {
      name: this.group?.name || this.groupForm.value.name.trim(),
      share_patients: this.sharePatients,
      share_procedures: this.shareProcedures,
      share_providers: this.shareProviders
    };

    const sub = this.clinicGroupService.updateGroup(this.groupId, payload).subscribe({
      next: (updated) => {
        this.group = updated;
        this.sharePatients = updated.share_patients || false;
        this.shareProcedures = updated.share_procedures || false;
        this.shareProviders = updated.share_providers || false;
        this.savingSharing = false;
        this.store.dispatch(setWarningText('Configurações de compartilhamento atualizadas!', 200));
        // Reload clinics context so sharing flags propagate
        this.contextService.loadClinicsAndSetDefault(true).subscribe();
      },
      error: (error) => {
        console.error('Erro ao atualizar compartilhamento:', error);
        this.store.dispatch(setWarningText(error?.message || 'Erro ao atualizar compartilhamento.', error?.status || 500));
        this.savingSharing = false;
      }
    });
    this.subscriptions.push(sub);
  }

  openAssignModal(): void {
    this.showAssignModal = true;
    this.assignSearchTerm = '';
    this.loadAvailableClinics();
  }

  closeAssignModal(): void {
    this.showAssignModal = false;
    this.availableClinics = [];
    this.filteredAvailableClinics = [];
  }

  loadAvailableClinics(): void {
    this.loadingClinics = true;
    const sub = this.companyService.listCompanies(0, 999, 'ASC', 'corporateName', '').subscribe({
      next: (response: any) => {
        const all = response.data || response || [];
        // Filter out clinics already in this group
        const groupCnpjs = new Set((this.group?.clinics || []).map((c: IClinicSummary) => c.cnpj));
        this.availableClinics = all.filter((c: any) => !groupCnpjs.has(c.cnpj));
        this.filterAvailableClinics();
        this.loadingClinics = false;
      },
      error: () => {
        this.loadingClinics = false;
      }
    });
    this.subscriptions.push(sub);
  }

  filterAvailableClinics(): void {
    if (!this.assignSearchTerm.trim()) {
      this.filteredAvailableClinics = this.availableClinics;
      return;
    }
    const term = this.assignSearchTerm.toLowerCase().trim();
    this.filteredAvailableClinics = this.availableClinics.filter((c: any) =>
      (c.corporate_name || '').toLowerCase().includes(term) ||
      (c.cnpj || '').includes(term)
    );
  }

  assignClinic(clinic: any): void {
    if (!this.groupId || this.assigningClinic) return;

    this.assigningClinic = true;
    const sub = this.clinicGroupService.assignClinic(this.groupId, { cnpj: clinic.cnpj }).subscribe({
      next: (updated) => {
        this.group = updated;
        this.assigningClinic = false;
        this.store.dispatch(setWarningText('Clínica vinculada ao grupo com sucesso!'));
        this.closeAssignModal();
        // Refresh clinics context so groupId propagates to other views
        this.contextService.loadClinicsAndSetDefault(true).subscribe();
      },
      error: (error) => {
        console.error('Erro ao vincular clínica:', error);
        this.assigningClinic = false;
        this.store.dispatch(setWarningText(error?.message || 'Erro ao vincular clínica.', error?.status || 500));
      }
    });
    this.subscriptions.push(sub);
  }

  // ─── Remove Clinic ───────────────────────────────────────────────

  openRemoveModal(clinic: IClinicSummary): void {
    this.clinicToRemove = clinic;
    this.showRemoveModal = true;
  }

  closeRemoveModal(): void {
    this.showRemoveModal = false;
    this.clinicToRemove = null;
  }

  confirmRemoveClinic(): void {
    if (!this.groupId || !this.clinicToRemove?.cnpj || this.removingClinic) return;

    this.removingClinic = true;
    const sub = this.clinicGroupService.removeClinic(this.groupId, this.clinicToRemove.cnpj).subscribe({
      next: (updated) => {
        this.group = updated;
        this.removingClinic = false;
        this.store.dispatch(setWarningText('Clínica removida do grupo com sucesso!'));
        this.closeRemoveModal();
        // Refresh clinics context so groupId changes propagate to other views
        this.contextService.loadClinicsAndSetDefault(true).subscribe();
      },
      error: (error) => {
        console.error('Erro ao remover clínica:', error);
        this.removingClinic = false;
        this.store.dispatch(setWarningText(error?.message || 'Erro ao remover clínica.', error?.status || 500));
      }
    });
    this.subscriptions.push(sub);
  }

  // ─── Enter Clinic ─────────────────────────────────────────────────

  enterClinic(clinic: IClinicSummary): void {
    const clinicContext: IClinicContext = {
      id: '',
      cnpj: clinic.cnpj,
      name: clinic.corporate_name,
      groupId: this.groupId,
      groupName: this.group?.name
    };
    this.contextService.setActiveClinic(clinicContext);
    this.router.navigate(['/main/v2']);
  }

  // ─── Delete Group ─────────────────────────────────────────────────

  openDeleteModal(): void {
    this.showDeleteModal = true;
  }

  closeDeleteGroupModal(): void {
    this.showDeleteModal = false;
  }

  confirmDeleteGroup(): void {
    if (!this.groupId) return;

    this.loading = true;
    this.showDeleteModal = false;

    const sub = this.clinicGroupService.deleteGroup(this.groupId).subscribe({
      next: () => {
        this.store.dispatch(setWarningText('Grupo excluído com sucesso!'));
        this.router.navigate(['/main/v2/clinic-groups']);
      },
      error: (error) => {
        console.error('Erro ao excluir grupo:', error);
        this.store.dispatch(setWarningText(error?.message || 'Erro ao excluir grupo.', error?.status || 500));
        this.loading = false;
      }
    });
    this.subscriptions.push(sub);
  }

  goBack(): void {
    this.router.navigate(['/main/v2/clinic-groups']);
  }
}
